/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from 'react';

const DEFAULT_NAV_HEIGHT = 80;

/**
 * Publishes the mobile bottom-navigation height as a CSS custom property so
 * page content can reserve space for it.
 *
 * Sets `--bottom-nav-height` on the document root while active and resets it to
 * `0px` on cleanup or when deactivated.
 *
 * The height is a parameter because two different bottom navigations use this:
 * the pre/post-launch `MobileNav` and the agency shell's own bar, which is
 * shorter. Anything anchored to the viewport rather than to the shell's flow
 * -- the floating action buttons, the consent banner -- reads this token to
 * clear whichever bar is actually on screen, so publishing one nav's height
 * while the other is rendered leaves a button hovering over the bar or a gap
 * under it.
 *
 * @param active Whether the bottom navigation is currently rendered.
 * @param heightPx The rendered bar's height in px, excluding the safe area.
 */
export function useBottomNavHeight(active: boolean, heightPx: number = DEFAULT_NAV_HEIGHT): void {
  useEffect(() => {
    if (!active) return;
    const el = document.documentElement;
    el.style.setProperty(
      '--bottom-nav-height',
      `calc(${heightPx}px + env(safe-area-inset-bottom, 0px))`,
    );
    return () => {
      el.style.setProperty('--bottom-nav-height', '0px');
    };
  }, [active, heightPx]);
}
